import React, { useEffect, useRef, useState } from 'react';
import { FormField } from './FormField';
import styles from './AsyncInputSearchField.module.css';

const queryDebounceTime = 400;

export const AsyncInputSearchField = ({
  name,
  label,
  validate = [],
  formFieldProps,
  options,
  // `currentQuery` is the last query used to fetch results from the API. note that this is not
  //    necessarily == the user's current input. if `currentQuery` changes, this component then
  //    knows to remove the loading indicator and that it has a new set of dropdown results
  currentQuery,
  onUpdateQuery,
}) => {
  const [loading, setLoading] = useState(false);
  const [dropdownActive, setDropdownActive] = useState(false);
  const formFieldRef = useRef(null);
  const searchFieldName = `search__${name}`;

  const prevQuery = useRef(currentQuery);
  if (currentQuery !== prevQuery.current) {
    prevQuery.current = currentQuery;
    setLoading(false);
    setDropdownActive(true);
  }

  const renderDropdownOptions = () => {
    if (loading) {
      return <li className={`${styles.dropdownItem} ${styles.dropdownItemLoading}`}>Loading...</li>;
    }
    return options.map((option) => (
      <li className={styles.dropdownItem} data-value={option.value} onClick={handleSelectOption(option)}>
        {option.label}
      </li>
    ));
  };

  const handleChangeDebouncer = useRef();
  const handleOnChange = (userInput) => {
    if (!userInput) return;
    formFieldProps.updateValue(name, undefined);
    setLoading(true);
    if (handleChangeDebouncer.current) {
      clearTimeout(handleChangeDebouncer.current);
    }
    handleChangeDebouncer.current = setTimeout(() => {
      if (userInput === currentQuery) return;
      onUpdateQuery && onUpdateQuery(userInput);
    }, queryDebounceTime);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (formFieldRef.current && !formFieldRef.current.contains(e.target)) {
        setDropdownActive(false);
        setLoading(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleSelectOption = (option) => () => {
    formFieldProps.updateValue(searchFieldName, option.label);
    formFieldProps.updateValue(name, option.value);
    setDropdownActive(false);
    formFieldProps.updateError(searchFieldName, null);
  };

  return (
    <div className={styles.wrapper} ref={formFieldRef}>
      <FormField
        name={searchFieldName}
        label={label}
        component="InputField"
        validate={validate}
        onChange={handleOnChange}
        {...formFieldProps}
      />
      {loading || (options.length && dropdownActive) ? (
        <ul className={`asyncInputSearchField ${styles.dropdown}`}>{renderDropdownOptions()}</ul>
      ) : (
        <></>
      )}
    </div>
  );
};
